export const WEAPON = {
  id: 'amr',
  label: 'M82A1',
  class: 'sniper',
  caliber: '12.7x99',
  /**
   * The top of the precision ladder, and a deliberate gap-filler in the
   * armory's price curve: 650/650 (sawn-off, Uzi), 800 (SMG), 850 (knife),
   * 1100 (AKM), 1200/1200 (shotgun, revolver), 1900 (DMR), 2000 (bolt gun),
   * 2200 (LMG), then nothing at all until the launcher at 3200. 2800 goes in
   * that hole. It buys the only weapon in the game that reliably removes a
   * target with one body hit, through cover, at any range the maps offer —
   * paid for with a 60 rpm practical cadence, a 10-round magazine, the
   * slowest ADS and draw in the game, and a hip-fire cone no one should ever
   * use.
   */
  price: 2800,
  /**
   * Audio. `AudioSystem._onFire` resolves a profile from `weapon.audio ??
   * weapon.id ?? weapon.name` (see src/audio/weapons.js `resolveProfile`),
   * and the id 'amr' matches none of its patterns, so it would fall through
   * to the 5.56 rifle profile — a .50 BMG with a carbine's crack. Naming the
   * profile directly is the hook the audio system already provides for
   * exactly this.
   */
  audio: 'sniper',
  /* --- fire control ---
   * Semi-auto: a short-recoil action that cycles itself, unlike the bolt
   * gun's manual lift-pull-push-lock. `rpm` is the PRACTICAL aimed cadence a
   * shooter can hold with a 14 kg rifle in .50 — recoil recovery and sight
   * re-acquisition, not a mechanical limit, since the action itself will
   * cycle far faster. 60 against the bolt gun's ~45 manual-cycle stand-in and
   * the DMR's 550: this weapon's advantage over the M700-LR is that it does
   * not break the sight picture between shots, not that it is fast.
   */
  rpm: 60,
  modes: ['semi'],
  burstCount: 1,
  burstRpm: 60,
  burstDelay: 0.1,
  /* --- ammunition ---
   * 10 rounds, the M82's real magazine. 30 in reserve is three spares, which
   * is as much .50 BMG as anyone carries — each round is 118 g of brass,
   * copper and lead.
   */
  magSize: 10,
  reserve: 30,
  /* --- terminal ballistics ---
   * Real .50 BMG M33 ball out of the 737 mm barrel: ~853 m/s. Everything else
   * follows from a 42 g bullet with a ballistic coefficient no small-arms
   * round comes near — it holds velocity where 7.62 has stopped mattering, so
   * dragK is the lowest in the game (0.14 against the bolt gun's 0.22),
   * maxRange is 1200 m and dropoff is 0.86, i.e. it still lands 86% of full
   * damage at the end of that. penetration 2.6 is nearly double the bolt
   * gun's 1.4: this is a round designed to go through an engine block, and
   * the one weapon here that treats cover as a suggestion.
   *
   * damage 140 is the armory's own DMG headroom (see src/ui/armory.js
   * statPct), so this weapon pegs that bar and nothing can exceed it without
   * the denominator moving. Against the 1000 HP baseline in player/tuning.js
   * it is 40% more per shot than the M700-LR's 95 — a decisive but not
   * instant hit, in line with how the rest of this game's damage is scaled.
   */
  muzzleVelocity: 853,
  damage: 140,
  penetration: 2.6,
  dropoff: 0.86,
  maxRange: 1200,
  dragK: 0.14,
  tracerEvery: 1,
  /* --- accuracy (degrees) ---
   * The widest hip cone in the game (5.4 against the bolt gun's 4.0) and the
   * tightest aimed one bar none (0.06 against 0.05 — a semi-auto action loses
   * a hair of what a bedded bolt action keeps). Firing it without aiming is
   * meant to be a mistake.
   */
  spreadHip: 5.4,
  spreadAds: 0.06,
  spreadPerShot: 0.95,
  spreadMax: 6.8,
  spreadDecay: 4.2,
  /* --- recoil ---
   * The hardest-kicking weapon in the game and still softer than the
   * cartridge deserves, which is the honest answer: a real M82 recoils like a
   * 12-gauge because its barrel moves 25 mm backwards inside the receiver and
   * its brake dumps most of the rest sideways. So `kickBack` is huge (0.052,
   * against the bolt gun's 0.028 — that is the barrel assembly travelling,
   * and it is the thing the player should feel), while `pitch` at 0.026 is
   * only 1.6x the bolt gun's rather than the 4x the energy ratio implies.
   * `patternLength` 10 matches the magazine, so a full magazine is one
   * complete, learnable pattern.
   */
  recoil: {
    pitch: 0.026,
    yaw: 0.0042,
    kickBack: 0.052,
    kickUp: 0.019,
    roll: 0.058,
    punch: 0.88,
    freq: 4.8,
    damping: 0.58,
    patternLength: 10,
    patternSeed: 0x50bb12,
    climbShape: [1.0],
    drift: 0.22,
  },
  /* --- handling (seconds) ---
   * Slowest in the game on every axis, because 14 kg is 4x the carbine and
   * 2x the LMG. adsFov 0.28 is the deepest magnification here (the bolt gun
   * is 0.32, the DMR 0.5): a big scope on a long rail.
   */
  adsTime: 0.56,
  adsFov: 0.28,
  viewFov: 0.88,
  reloadTac: 3.8,
  reloadEmpty: 4.9,
  inspectTime: 4.4,
  drawTime: 1.05,
  holsterTime: 0.68,
  /* --- pose ---
   * SOLVED FROM THE BORE AXIS, by the method in defs/rifle.js's derivation
   * comment rather than by copying its numbers. Weapon-local geometry taken
   * off models/amr.js's own build: muzzle crown (0, 0.082, -1.062), scope
   * ocular (0, 0.180, +0.039), top of the scope housing (0, 0.214, -0.020),
   * carry handle crest (0, 0.176, -0.200), magwell mouth (0, 0.042, -0.165),
   * magazine floorplate (0, -0.164, -0.179) once seated, folded bipod foot
   * (0.017, 0.032, -0.606), support wrist (-0.056, 0.018, -0.200).
   *
   * Constraints, in the same order rifle.js applies them:
   *   1. bore axis LEFT of view-forward and nose-down, converging on the
   *      crosshair: rx = -0.050, ry = +0.072. rx is the value rifle.js,
   *      smg.js, sniper.js and lmg.js all share, because it describes the
   *      eye-to-weapon geometry rather than the gun. ry is this weapon's own
   *      and it is SMALLER than every other two-handed gun's (the carbine
   *      0.081, the bolt gun and LMG 0.078): convergence angle times barrel
   *      length is a sideways displacement, and with 1.06 m from the hand to
   *      the crown even 4.1 deg already walks the muzzle 66 mm left. At the
   *      carbine's 0.081 the crown crosses the view axis entirely and ends up
   *      on the wrong side of the screen.
   *   2. rolled 7.45 deg (rz = -0.13, the bolt gun's and the LMG's value) so
   *      the LEFT flank — the side carrying the rollmark — faces the camera
   *      and the rail deck turns edge-on.
   *   3. muzzle crown visible and heading for the middle of the frame.
   *      rifle.js's literal window (x 1050-1300) is a 0.75 m gun's number: a
   *      long barrel at a converging angle necessarily lands nearer the
   *      centre, and sniper.js's own crown projects to (1002, 666). This one
   *      lands at (990, 653), i.e. in the same place that gun's does.
   *   4. scope ocular below and right of screen centre.
   *   5. magazine in the lower-right frame.
   *
   * z = -0.40 is this weapon's own number and it is the one that is actually
   * solved rather than carried over. The binding constraint is VERTICAL
   * extent, not length: scope housing to magazine floorplate is 378 mm here
   * against the M4A1's 291 mm from ocular to floorplate, a ratio of 1.30, and
   * 1.30 x the carbine's -0.30 is -0.39. -0.40 is that, rounded away from the
   * frame edge. It is also the limit the other way: the support wrist then
   * sits 597 mm downrange of the eye, just inside the ~620 mm that
   * models/rifle.js measures for a 572 mm arm off a shoulder 200 mm from the
   * eye (sniper.js's own support wrist is at 606 mm), so the elbow keeps a
   * visible bend instead of the two-bone solve clamping straight.
   *
   * Projected at a 60 deg vertical FOV (half-height 0.5774|z|, half-width
   * 1.0265|z|), which is the check rifle.js's comment runs:
   *   muzzle -> (990, 653)        bipod foot -> (1043, 731)
   *   carry handle -> (1145, 603) scope ocular -> (1307, 603)
   *   scope housing top -> (1259, 527)
   *   magwell mouth -> (1134, 823)  magazine floorplate -> (1088, 1159)
   *   support wrist -> (1030, 837)
   * so the weapon reads as one long diagonal from the magazine in the lower
   * right, up the receiver past the carry handle, out along the bipod to a
   * muzzle in the middle of the frame — and the floorplate falls only 79 px
   * below the frame against the M4A1's 365 px, despite the magazine being
   * 190 mm long, because the gun is far enough out.
   *
   * The stock is behind the eye and 142 mm off axis at a half-width of
   * 106 mm, so it leaves the frustum laterally rather than being clipped —
   * the condition rifle.js documents for its own butt pad.
   *
   * NOT VISUALLY VERIFIED: no capture was run this session (tools/shoot.mjs
   * needs Playwright, which this box cannot run). Every number above is a
   * projection of the model's own measured geometry, not a measurement off a
   * rendered frame.
   */
  hipPos: [0.112, -0.205, -0.4],
  hipRot: [-0.05, 0.072, -0.13],
  adsCant: [0, 0, 0.003],
  /* Eye to the rear lens. The scope is built with a 24 mm tube and a 132 mm
   * length (see models/amr.js's buildOptic call), one step up from the bolt
   * gun's 22 mm / 120 mm, so the aperture budget defs/rifle.js derives scales
   * with it: at 0.148 the ocular clear aperture subtends 0.1188 rad, which is
   * within half a percent of the 0.1193 the M700-LR's 0.135 produces against
   * its own smaller tube. Same sight picture, bigger glass. */
  eyeRelief: 0.148,
  /* Sprint and low-ready carry the same delta off the hip pose that rifle.js
   * uses, so the blend does not translate the weapon sideways on the way into
   * a sprint; the rotations are the shared values every two-handed gun in the
   * game uses, because they describe how the RIG carries a slung weapon
   * rather than the weapon itself. */
  sprintPos: [0.084, -0.282, -0.375],
  sprintRot: [-0.4, 0.6, 0.2],
  lowReadyPos: [0.106, -0.3, -0.389],
  lowReadyRot: [-0.46, 0.125, -0.09],
  /* The most sway and the least bob of anything in the game, for the reason
   * defs/lmg.js gives for landing between the sniper and everything else:
   * sheer mass fights a standing shooter's stance harder than magnification
   * does, and that same mass damps the vertical bounce of a stride. */
  swayScale: 1.3,
  bobScale: 0.68,
  // Matches models/amr.js's magSize.len exactly.
  magLen: 0.19,
};

export const loadModel = () => import('../models/amr.js').then((m) => m.buildAmr);
